import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../css/dcrLeaveType.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";

const LogoutAllUsers = () => {
  const navigate = useNavigate();
  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);

  const [message, setMessage] = useState("");
  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [endTime, setEndTime] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toTimeString().slice(0, 5);
  });

  const [lockedUsers, setLockedUsers] = useState([]);
  const [loadingLockedUsers, setLoadingLockedUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLockedUsers, setSelectedLockedUsers] = useState([]);
  const [unlocking, setUnlocking] = useState(false);

  // Simplified designation key extraction
  const getDesignationKey = useCallback((desig) => {
    if (!desig) return "";
    if (typeof desig === "string") return desig;
    return (
      desig.code || desig.short_name || desig.name || desig.id?.toString() || ""
    );
  }, []);

  // Fetch locked users
  const fetchLockedUsers = useCallback(async () => {
    setLoadingLockedUsers(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/locked-users`, {
        headers: getAuthHeaders(),
      });
      const lockedUsersData = Array.isArray(res.data?.locked_users)
        ? res.data.locked_users
        : [];
      setLockedUsers(lockedUsersData);
    } catch (err) {
      console.error(
        "Failed to fetch locked users:",
        err.response?.data || err.message
      );
      alert("Failed to fetch locked users");
      setLockedUsers([]);
    } finally {
      setLoadingLockedUsers(false);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [divRes, stateRes, desigRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/designations/`, {
            headers: getAuthHeaders(),
          }),
        ]);

        setDivisions(Array.isArray(divRes.data) ? divRes.data : []);
        setStates(Array.isArray(stateRes.data) ? stateRes.data : []);
        setDesignations(Array.isArray(desigRes.data) ? desigRes.data : []);
      } catch (err) {
        console.error(
          "Error fetching initial data:",
          err.response?.data || err.message
        );
        alert("Failed to load filter options");
      }
    };

    fetchInitialData();
    fetchLockedUsers();
  }, [fetchLockedUsers]);

  const handleCheckboxChange = useCallback((value, selectedList, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
  }, []);

  const handleSelectAll = useCallback(
    (items, selectedList, setList, key) => {
      if (!Array.isArray(items) || items.length === 0) {
        setList([]);
        return;
      }

      if (selectedList.length === items.length) {
        setList([]);
      } else {
        const values = items
          .map((item) => {
            if (key === "division") return item.name;
            if (key === "state") return item.id.toString();
            if (key === "designation") return getDesignationKey(item);
            return item.id.toString();
          })
          .filter((value) => value !== ""); // Filter out empty values
        setList(values);
      }
    },
    [getDesignationKey]
  );

  // Check if all items are selected
  const isAllSelected = useCallback(
    (items, selectedList, key) => {
      if (!Array.isArray(items) || items.length === 0) return false;

      const expectedValues = items
        .map((item) => {
          if (key === "division") return item.name;
          if (key === "state") return item.id.toString();
          if (key === "designation") return getDesignationKey(item);
          return item.id.toString();
        })
        .filter((value) => value !== "");

      return (
        expectedValues.length > 0 &&
        expectedValues.every((value) => selectedList.includes(value))
      );
    },
    [getDesignationKey]
  );

  // Check if some items are selected (for indeterminate state)
  const isSomeSelected = useCallback(
    (items, selectedList, key) => {
      if (!Array.isArray(items) || items.length === 0) return false;

      const expectedValues = items
        .map((item) => {
          if (key === "division") return item.name;
          if (key === "state") return item.id.toString();
          if (key === "designation") return getDesignationKey(item);
          return item.id.toString();
        })
        .filter((value) => value !== "");

      return (
        selectedList.length > 0 && !isAllSelected(items, selectedList, key)
      );
    },
    [isAllSelected, getDesignationKey]
  );

  // Lock users
  const handleSubmit = async () => {
    if (
      selectedDivisions.length === 0 &&
      selectedStates.length === 0 &&
      selectedDesignations.length === 0
    ) {
      alert(
        "Please select at least one division, state or designation to lock"
      );
      return;
    }

    // Convert state IDs back to numbers for API
    const stateIds = selectedStates
      .map((s) => {
        const num = parseInt(s, 10);
        return isNaN(num) ? null : num;
      })
      .filter((n) => n !== null);

    const payload = {
      state_id: stateIds,
      divisions: selectedDivisions,
      designations: selectedDesignations,
      message,
      start_date: startDate,
      end_time: endTime,
    };

    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/api/lock-users`, payload, {
        headers: getAuthHeaders(),
      });
      alert("Users locked successfully");
      fetchLockedUsers();

      // Clear selections after successful lock
      setSelectedDivisions([]);
      setSelectedStates([]);
      setSelectedDesignations([]);
      setMessage("");
    } catch (err) {
      console.error("Lock users failed:", err.response?.data || err.message);
      alert("Failed to lock users. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Unlock users
  const unlockSelected = async () => {
    if (selectedLockedUsers.length === 0) {
      alert("Please select at least one user to unlock");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to unlock ${selectedLockedUsers.length} users?`
      )
    )
      return;

    setUnlocking(true);
    try {
      const payload = {
        state_id: [...new Set(selectedLockedUsers.map((u) => u.state_id))],
        divisions: [...new Set(selectedLockedUsers.map((u) => u.division))],
        designations: [
          ...new Set(selectedLockedUsers.map((u) => u.designation)),
        ],
      };

      await axios.post(`${BASE_URL}/api/unlock-users`, payload, {
        headers: getAuthHeaders(),
      });

      alert("Users unlocked successfully");
      setSelectedLockedUsers([]);
      fetchLockedUsers();
    } catch (err) {
      console.error("Unlock failed:", err.response?.data || err.message);
      alert("Failed to unlock users. Please try again.");
    } finally {
      setUnlocking(false);
    }
  };

  // Select all locked users
  const handleSelectAllLockedUsers = useCallback(() => {
    if (selectedLockedUsers.length === lockedUsers.length) {
      setSelectedLockedUsers([]);
    } else {
      setSelectedLockedUsers([...lockedUsers]);
    }
  }, [lockedUsers, selectedLockedUsers.length]);

  // Toggle selection of a single locked user
  const toggleLockedUserSelection = useCallback((user) => {
    setSelectedLockedUsers((prev) =>
      prev.some((u) => u.user_id === user.user_id)
        ? prev.filter((u) => u.user_id !== user.user_id)
        : [...prev, user]
    );
  }, []);

  // Set indeterminate state for checkboxes
  useEffect(() => {
    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"][data-indeterminate]'
    );
    checkboxes.forEach((checkbox) => {
      checkbox.indeterminate = checkbox.dataset.indeterminate === "true";
    });
  });

  return (
    <div className="dcr-leave-type-main-container">
      <div className="dcr-leave-type-page-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Manage </span>
          <span className="text-blue-500">User locks</span>
        </h1>

        {/* Filters */}
        <div className="dcr-leave-type-filter-container">
          {/* Divisions */}
          <div className="dcr-leave-type-division-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Division</span>
              <input
                type="checkbox"
                checked={isAllSelected(
                  divisions,
                  selectedDivisions,
                  "division"
                )}
                data-indeterminate={isSomeSelected(
                  divisions,
                  selectedDivisions,
                  "division"
                )}
                onChange={() =>
                  handleSelectAll(
                    divisions,
                    selectedDivisions,
                    setSelectedDivisions,
                    "division"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-division-list">
              {divisions.map((d) => (
                <div key={d.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedDivisions.includes(d.name)}
                    onChange={() =>
                      handleCheckboxChange(
                        d.name,
                        selectedDivisions,
                        setSelectedDivisions
                      )
                    }
                  />
                  {d.name}
                </div>
              ))}
            </div>
          </div>

          {/* States */}
          <div className="dcr-leave-type-state-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>State</span>
              <input
                type="checkbox"
                checked={isAllSelected(states, selectedStates, "state")}
                data-indeterminate={isSomeSelected(
                  states,
                  selectedStates,
                  "state"
                )}
                onChange={() =>
                  handleSelectAll(
                    states,
                    selectedStates,
                    setSelectedStates,
                    "state"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-state-list">
              {states.map((s) => (
                <div key={s.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedStates.includes(s.id.toString())}
                    onChange={() =>
                      handleCheckboxChange(
                        s.id.toString(),
                        selectedStates,
                        setSelectedStates
                      )
                    }
                  />
                  {s.name}
                </div>
              ))}
            </div>
          </div>

          {/* Designations */}
          <div className="dcr-leave-type-designation-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Designation</span>
              <input
                type="checkbox"
                checked={isAllSelected(
                  designations,
                  selectedDesignations,
                  "designation"
                )}
                data-indeterminate={isSomeSelected(
                  designations,
                  selectedDesignations,
                  "designation"
                )}
                onChange={() =>
                  handleSelectAll(
                    designations,
                    selectedDesignations,
                    setSelectedDesignations,
                    "designation"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-designation-list">
              {designations.map((d) => {
                const key = getDesignationKey(d);
                return key ? (
                  <div key={d.id} className="dcr-leave-type-checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedDesignations.includes(key)}
                      onChange={() =>
                        handleCheckboxChange(
                          key,
                          selectedDesignations,
                          setSelectedDesignations
                        )
                      }
                    />
                    {d.name || key}
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="dcr-leave-type-form-row">
          <div className="dcr-leave-type-form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="dcr-leave-type-form-group">
            <label>End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          <div className="dcr-leave-type-form-group">
            <label>Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="3"
              placeholder="Enter lock message"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="dcr-leave-type-button-row">
          <CustomButton
          className="whitespace-nowrap"
            variant="save"
            label={submitting ? "Locking..." : "Lock"}
            onClick={handleSubmit}
            disabled={submitting}
          />

          {lockedUsers.length > 0 && (
            <CustomButton
              onClick={unlockSelected}
              disabled={unlocking || selectedLockedUsers.length === 0}
              label={
                unlocking
                  ? "Unlocking..."
                  : `Unlock Selected (${selectedLockedUsers.length})`
              }
              variant="cancel"
            />
          )}

          <CustomButton
            onClick={() => navigate(`/dashboard`)}
            label={"Cancel"}
            variant="cancel"
          />
        </div>

        <hr className="my-6" />

        {/* Locked Users Table */}
        <h2 className="text-lg font-semibold mb-2">Locked users</h2>
        {loadingLockedUsers ? (
          <div>Loading locked users...</div>
        ) : lockedUsers.length === 0 ? (
          <div>No users are locked currently.</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedLockedUsers.length === lockedUsers.length &&
                        lockedUsers.length > 0
                      }
                      onChange={handleSelectAllLockedUsers}
                    />
                  </th>
                  <th>User ID</th>
                  <th>State</th>
                  <th>Division</th>
                  <th>Designation</th>
                </tr>
              </thead>
              <tbody>
                {lockedUsers.map((user, index) => (
                  <tr key={index} className="border-t">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLockedUsers.some(
                          (u) => u.user_id === user.user_id
                        )}
                        onChange={() => toggleLockedUserSelection(user)}
                      />
                    </td>
                    <td>{user.user_id}</td>
                    <td>{user.state_name || user.state_id}</td>
                    <td>{user.division}</td>
                    <td>{user.designation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoutAllUsers;
